/**
 * Inactivity auto-logoff — "if >15m inactivity, just log me off" (owner request).
 *
 * A single app-wide monitor that watches for ANY real user input (pointer, keyboard,
 * touch, wheel) at the window/DOM level — so it works no matter which Phaser scene is
 * active — and, after {@link DEFAULT_INACTIVITY_TIMEOUT_MS} of total silence, fires a
 * graceful logoff:
 *
 *   1) FLUSH the live session durably (no progress is lost), then
 *   2) clearProxyToken() (an idle tab stops holding the session + stops syncing), then
 *   3) return to the AuthGate (so the next play must re-enter the password).
 *
 * ANY activity RESETS the countdown, so an actively-playing owner is NEVER logged off.
 *
 * Mirrors the Autosave shape on purpose (the repo's established lifecycle pattern):
 *  - an injectable {@link InactivityHost} (timers + DOM events + a clock) so the whole
 *    thing is deterministically unit-testable with no real DOM / no real clock;
 *  - idempotent start()/stop() that tear down EVERY listener + the timer (no leaks, no
 *    stacked monitors across a re-init — the Phaser-reuse safety);
 *  - fully guarded: a throw in the logoff callback or an activity handler can never
 *    escape into the timer / event loop.
 *
 * The pure timer/idle DECISION logic lives in {@link decideInactivity} (Phaser-free,
 * DOM-free) and is unit-tested directly; the class is just the wiring around it.
 */

/** 15 minutes — the single tunable timeout. "If >15m inactivity, just log me off." */
export const DEFAULT_INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * How often the monitor wakes to CHECK the deadline (a coarse poll, not the precision
 * of the timeout). A check every ~30s means the logoff fires within ~30s of the true
 * 15-minute mark — plenty calm, and it spares us re-arming a timer on every mousemove.
 */
export const DEFAULT_INACTIVITY_CHECK_MS = 30 * 1000;

/**
 * The DOM events that count as REAL user activity. Listened to at window/DOM level
 * (capture phase, passive) so a tap/move/keypress/scroll resets the countdown no
 * matter which Phaser scene owns input at the time.
 */
export const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'touchstart',
  'wheel',
] as const;

/** The pure inputs the idle decision needs (no DOM, no Phaser). */
export interface InactivityState {
  /** Wall-clock ms of the last observed user activity. */
  readonly lastActivityAt: number;
  /** Wall-clock ms now. */
  readonly now: number;
  /** The inactivity budget in ms (e.g. {@link DEFAULT_INACTIVITY_TIMEOUT_MS}). */
  readonly timeoutMs: number;
}

/** The pure decision the monitor acts on each check. */
export interface InactivityDecision {
  /** True ⇒ the budget has elapsed with no activity; the caller should log off. */
  readonly shouldLogoff: boolean;
  /** ms remaining until logoff (0 when {@link shouldLogoff}); for diagnostics/tests. */
  readonly remainingMs: number;
  /** ms since the last activity (clamped at ≥0); for diagnostics/tests. */
  readonly idleMs: number;
}

/**
 * Pure idle decision: given when the player last acted, the clock, and the budget,
 * decide whether enough silence has passed to log off. Phaser-free and DOM-free so it
 * is trivially unit-testable.
 *
 * Robust to a clock that jumps BACKWARD (system clock change, a fake-clock test that
 * rewinds): a negative elapsed is clamped to 0 idle, so a backward jump never trips a
 * spurious logoff. The deadline is INCLUSIVE — reaching exactly `timeoutMs` of silence
 * logs off (so a 0ms budget would log off immediately; real budgets are minutes).
 */
export function decideInactivity(state: InactivityState): InactivityDecision {
  const { lastActivityAt, now, timeoutMs } = state;
  const idleMs = Math.max(0, now - lastActivityAt);
  const shouldLogoff = idleMs >= timeoutMs;
  const remainingMs = shouldLogoff ? 0 : timeoutMs - idleMs;
  return { shouldLogoff, remainingMs, idleMs };
}

/** Injectable host environment — defaults to the browser globals, stubbed in tests. */
export interface InactivityHost {
  setInterval: (handler: () => void, ms: number) => unknown;
  clearInterval: (handle: unknown) => void;
  /** The event target for activity hooks (window in the browser). */
  addEventListener?: (type: string, handler: () => void) => void;
  removeEventListener?: (type: string, handler: () => void) => void;
  /** Wall-clock now in ms (Date.now in the browser; a fake clock in tests). */
  now: () => number;
}

export interface InactivityOptions {
  /** Inactivity budget; defaults to {@link DEFAULT_INACTIVITY_TIMEOUT_MS}. */
  readonly timeoutMs?: number;
  /** Deadline poll period; defaults to {@link DEFAULT_INACTIVITY_CHECK_MS}. */
  readonly checkMs?: number;
  /** Host override (tests inject a fake; prod uses the real globals). */
  readonly host?: InactivityHost;
}

/**
 * A running inactivity monitor. {@link start} wires the activity listeners + the
 * deadline poll; {@link stop} tears every one of them down (idempotent). When the
 * budget elapses with no activity it calls the `onLogoff` callback EXACTLY ONCE and
 * stops itself (so it can't double-fire) — the caller's callback does the flush +
 * token-clear + AuthGate transition.
 *
 * The logoff callback and the activity handler are both guarded: a throw can never
 * escape into the timer / DOM event loop.
 */
export class InactivityMonitor {
  private readonly onLogoff: () => void;
  private readonly timeoutMs: number;
  private readonly checkMs: number;
  private readonly host: InactivityHost;

  private intervalHandle: unknown = null;
  private listening = false;
  private firing = false;
  private lastActivityAt: number;

  /** Bound once so add/removeEventListener pair on the SAME reference (clean teardown). */
  private readonly onActivity = (): void => {
    // While the logoff is being handled, ignore late input so a stray pointer event
    // can't "rescue" a session we've already decided to end (it would also race the
    // teardown). Before that, any activity simply resets the countdown.
    if (this.firing) return;
    this.lastActivityAt = this.now();
  };

  private readonly onCheck = (): void => {
    if (this.firing) return;
    const decision = decideInactivity({
      lastActivityAt: this.lastActivityAt,
      now: this.now(),
      timeoutMs: this.timeoutMs,
    });
    if (decision.shouldLogoff) this.fire();
  };

  constructor(onLogoff: () => void, options: InactivityOptions = {}) {
    this.onLogoff = onLogoff;
    this.timeoutMs = options.timeoutMs ?? DEFAULT_INACTIVITY_TIMEOUT_MS;
    this.checkMs = options.checkMs ?? DEFAULT_INACTIVITY_CHECK_MS;
    this.host = options.host ?? defaultHost();
    this.lastActivityAt = this.now();
  }

  private now(): number {
    return this.host.now();
  }

  /** Begin the activity listeners + the deadline poll. Idempotent (a 2nd call no-ops). */
  start(): void {
    if (this.intervalHandle != null || this.listening) return; // already running
    // Treat start() itself as activity so a freshly-started monitor doesn't carry a
    // stale (pre-construction) timestamp into an immediate logoff.
    this.lastActivityAt = this.now();
    this.firing = false;

    this.intervalHandle = this.host.setInterval(this.onCheck, this.checkMs);

    const add = this.host.addEventListener?.bind(this.host);
    if (add) {
      for (const evt of ACTIVITY_EVENTS) add(evt, this.onActivity);
      this.listening = true;
    }
  }

  /**
   * Stop the poll and remove EVERY activity listener (no stacked monitors, no leaked
   * handlers across a re-init). Idempotent. Does NOT itself log off.
   */
  stop(): void {
    if (this.intervalHandle != null) {
      this.host.clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
    if (this.listening) {
      const remove = this.host.removeEventListener?.bind(this.host);
      if (remove) {
        for (const evt of ACTIVITY_EVENTS) remove(evt, this.onActivity);
      }
      this.listening = false;
    }
  }

  /**
   * Manually note activity (e.g. a Phaser-internal interaction the DOM listeners would
   * miss). Equivalent to a real input: resets the countdown. No-op while firing.
   */
  notifyActivity(): void {
    this.onActivity();
  }

  /** ms remaining until logoff at the current moment (test/diagnostic introspection). */
  remainingMs(): number {
    return decideInactivity({
      lastActivityAt: this.lastActivityAt,
      now: this.now(),
      timeoutMs: this.timeoutMs,
    }).remainingMs;
  }

  /** True while the monitor is polling (test introspection). */
  get isRunning(): boolean {
    return this.intervalHandle != null;
  }

  /** Fire the logoff exactly once, then stop the monitor so it can never re-fire. */
  private fire(): void {
    if (this.firing) return;
    this.firing = true;
    // Stop FIRST so no further check/activity can interleave with the logoff handling;
    // the callback owns the flush + token-clear + transition.
    this.stop();
    try {
      this.onLogoff();
    } catch {
      // The logoff callback is best-effort; a throw must never escape into the loop.
      // (The token-clear / flush inside it are themselves guarded; this is belt-and-
      // braces so even a synchronous bug there can't crash the page.)
    }
  }
}

/**
 * The default browser host: real timers + window activity events (capture + passive so
 * they observe input regardless of scene focus and never block scrolling) + Date.now.
 * In a non-DOM environment the event hooks are simply absent — the poll still runs.
 */
function defaultHost(): InactivityHost {
  const win = typeof window !== 'undefined' ? window : undefined;
  return {
    setInterval: (handler, ms) => setInterval(handler, ms),
    clearInterval: (handle) => clearInterval(handle as ReturnType<typeof setInterval>),
    now: () => Date.now(),
    ...(win
      ? {
          addEventListener: (type, handler) =>
            win.addEventListener(type, handler, { capture: true, passive: true }),
          removeEventListener: (type, handler) =>
            win.removeEventListener(type, handler, { capture: true }),
        }
      : {}),
  };
}
